(() => {
  const normalize = (value) => (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  // Existing Echo products stay mapped for backwards compatibility.
  // Brand detection also reads the visible product title after 'Inspired By', so
  // products using the normal Echo title format can join/create brand buttons
  // without changing Vendor or repurposing existing fragrance tags.
  const productBrands = {
    'vanilla dust': 'Matière Première',
    'vanilla latte': 'Giardini di Toscana',
    'dark ocean': 'Lorenzo Pazzaglia',
    'desert tonka': 'Montale',
    'night club': 'Armaf',
    'citrus boise': 'Mancera',
    'genius ginger': 'Goldfield & Banks',
    'musk powder': 'Attar Al Has',
    'grande clouds': 'Ariana Grande',
    'miss d': 'Dior',
    'sungria soaked': 'Lorenzo Pazzaglia',
    'summer smasher': 'Lorenzo Pazzaglia',
    'vanished cherry': 'Tom Ford',
    'silence': 'Lorenzo Pazzaglia',
    'wifey': 'Second Wife',
    'aqua man': 'Ex Nihilo',
    'famous': 'Paco Rabanne',
    'billion': 'Paco Rabanne',
    'freaky': 'Byredo',
    'dark wood': 'Penhaligon’s',
    'halfeti': 'Penhaligon’s',
    'red 540': 'Maison Francis Kurkdjian',
    'zanzibar stories': 'Memoirs of a Perfume Collector',
    'jolly dust': 'Narcotica',
    'city stars': 'Clive Christian',
    'apex': 'Creed',
    'sporty': 'Chanel',
    'martian': 'Mugler',
    'midnight': 'Jo Malone',
    'midnight tonka': 'Jo Malone',
    'taormina tangerine': 'Tom Ford'
  };

  const brandDisplayOverrides = {
    'matiere premiere': 'Matière Première',
    'goldfield banks': 'Goldfield & Banks',
    'penhaligons': 'Penhaligon’s',
    'maison francis kurkdjian': 'Maison Francis Kurkdjian'
  };

  const ignoredVendors = new Set([
    '', 'echo', 'echo fragrances', 'echo fragrance', 'echo15', 'shopify'
  ]);

  const cleanDisplay = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const canonicalDisplay = (value) => {
    const display = cleanDisplay(value);
    const key = normalize(display);
    return brandDisplayOverrides[key] || display;
  };

  const brandFromTags = (rawTags) => {
    const tags = String(rawTags || '').split('||').map(cleanDisplay).filter(Boolean);
    for (const tag of tags) {
      const match = tag.match(/^(?:brand|designer|house|inspired\s*brand)\s*:\s*(.+)$/i);
      if (match && match[1]) return canonicalDisplay(match[1]);
    }
    return '';
  };

  const mappedBrand = (source) => {
    const text = normalize(source);
    // Longer names first avoids accidental short-name collisions.
    const names = Object.keys(productBrands).sort((a, b) => b.length - a.length);
    for (const productName of names) {
      if (text.includes(normalize(productName))) return productBrands[productName];
    }
    return '';
  };


  const knownBrands = [
    'Matière Première','Giardini di Toscana','Lorenzo Pazzaglia','Montale','Armaf',
    'Mancera','Goldfield & Banks','Attar Al Has','Ariana Grande','Dior','Tom Ford',
    'Second Wife','Ex Nihilo','Paco Rabanne','Byredo','Penhaligon’s',
    'Maison Francis Kurkdjian','Memoirs of a Perfume Collector','Narcotica',
    'Clive Christian','Creed','Chanel','Mugler','Jo Malone'
  ];

  const brandFromInspiredTitle = (rawTitle) => {
    const title = cleanDisplay(rawTitle);
    if (!title) return '';
    const marker = /\binspired\s+by\b/i;
    const match = marker.exec(title);
    if (!match) return '';
    const inspired = title.slice(match.index + match[0].length).trim();
    const inspiredNorm = normalize(inspired);
    if (!inspiredNorm) return '';

    // Match any house already present in the catalogue, regardless of where it
    // appears in the inspired-by wording (e.g. "Halfeti Penhaligon's").
    const dynamicKnown = knownBrands.slice().sort((a,b) => b.length - a.length);
    for (const brand of dynamicKnown) {
      if (inspiredNorm.includes(normalize(brand))) return canonicalDisplay(brand);
    }

    // Future/new houses can be created automatically when the inspired-by text
    // separates fragrance and house in the common "Fragrance — Brand" or
    // "Fragrance | Brand" form. This keeps Vendor and existing tags untouched.
    const parts = inspired.split(/\s+(?:—|–|\||\/\/|::)\s+/).map(cleanDisplay).filter(Boolean);
    if (parts.length > 1) return canonicalDisplay(parts[parts.length - 1]);
    return '';
  };

  const getBrand = (card) => {
    const explicit = canonicalDisplay(card.dataset.brandSearchExplicit);
    if (explicit) return explicit;

    const titleBrand = brandFromInspiredTitle(card.dataset.brandSearchTitle);
    if (titleBrand) return titleBrand;

    const tagBrand = brandFromTags(card.dataset.brandSearchTags);
    if (tagBrand) return tagBrand;

    // Map legacy Echo products from the PRODUCT TITLE ONLY. Using the full
    // card text can accidentally pick up fragrance names from selectors/options
    // inside another card and make that product appear under the wrong brand.
    const title = card.dataset.brandSearchTitle || '';
    const mapped = mappedBrand(title);
    if (mapped) return mapped;

    const vendor = canonicalDisplay(card.dataset.brandSearchVendor);
    if (vendor && !ignoredVendors.has(normalize(vendor))) return vendor;

    return '';
  };

  const createBrandButtons = (root, cards) => {
    const wrap = root.querySelector('[data-brand-buttons]') || root.querySelector('.echo-brand-search__buttons');
    if (!wrap) return [];

    // Rebuild everything except ALL so Shopify section reloads and newly-added
    // products cannot leave stale brand buttons behind.
    wrap.querySelectorAll('[data-brand-filter]:not([data-brand-filter=""])').forEach((button) => button.remove());

    const brands = new Map();
    cards.forEach((card) => {
      const display = getBrand(card);
      const key = normalize(display);
      card.dataset.brandSearchBrand = key;
      card.dataset.brandSearchBrandDisplay = display;
      if (key) {
        if (!brands.has(key)) brands.set(key, { display, count: 0 });
        brands.get(key).count += 1;
      }
    });

    Array.from(brands.entries())
      .sort((a, b) => a[1].display.localeCompare(b[1].display, undefined, { sensitivity: 'base' }))
      .forEach(([key, info]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'echo-brand-chip';
        button.dataset.brandFilter = key;

        const label = document.createElement('span');
        label.className = 'echo-brand-chip__label';
        label.textContent = info.display;

        const count = document.createElement('span');
        count.className = 'echo-brand-chip__count';
        count.textContent = `${info.count} ${info.count === 1 ? 'product' : 'products'}`;
        count.setAttribute('aria-label', `${info.count} ${info.count === 1 ? 'product' : 'products'}`);

        button.append(label, count);
        wrap.appendChild(button);
      });

    return Array.from(wrap.querySelectorAll('[data-brand-filter]'));
  };

  const init = (root) => {
    if (!root) return;

    const targetId = root.dataset.brandSearchTarget;
    const grid = targetId
      ? document.getElementById(targetId)
      : (root.closest('section') || document).querySelector('[data-brand-search-grid]');
    if (!grid) return;

    const section = grid.closest('section') || document;
    const empty = section.querySelector('[data-brand-search-empty]');
    const cards = Array.from(grid.querySelectorAll('[data-brand-search-card]'));
    const chips = createBrandButtons(root, cards);
    if (!chips.length) return;

    // Remove prior handlers on Shopify editor reload by cloning the buttons.
    // Normal storefront load only runs once.
    root.dataset.brandSearchReady = 'true';
    let selectedBrand = '';
    let rangeFilter = 'all';
    const rangeButtons = Array.from(section.querySelectorAll('[data-range-filter]'));

    // Automatic count badges for catalogue filters. Counts use the exact same
    // rules as the filters themselves, so they stay in sync with tags and NEW.
    const newWindowSeconds = 30 * 24 * 60 * 60;
    const newFeatureStartSeconds = Date.parse('2026-09-01T00:00:00+01:00') / 1000;
    const isCardNew = (card) => {
      const createdAt = Number(card.dataset.productCreatedAt || 0);
      const nowSeconds = Date.now() / 1000;
      const productAge = nowSeconds - createdAt;
      return Number.isFinite(createdAt)
        && createdAt >= newFeatureStartSeconds
        && productAge >= 0
        && productAge <= newWindowSeconds;
    };
    const rangeCount = (filter) => cards.reduce((count, card) => {
      if (filter === 'all') return count + 1;
      if (filter === 'new' && isCardNew(card)) return count + 1;
      if (filter === 'popular' && card.dataset.productPopular === 'true') return count + 1;
      if (filter === 'samples' && card.dataset.productSamples === 'true') return count + 1;
      if (filter === 'bundle15' && card.dataset.productBundle15 === 'true') return count + 1;
      if (filter === 'bundle30' && card.dataset.productBundle30 === 'true') return count + 1;
      if (filter === 'hybrid' && card.dataset.productHybrid === 'true') return count + 1;
      if (filter === 'male' && card.dataset.productMale === 'true') return count + 1;
      if (filter === 'female' && card.dataset.productFemale === 'true') return count + 1;
      if (filter === 'unisex' && card.dataset.productUnisex === 'true') return count + 1;
      return count;
    }, 0);
    const updateRangeCounts = () => {
      rangeButtons.forEach((button) => {
        let badge = button.querySelector('.echo-range-filter__count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'echo-range-filter__count';
          badge.setAttribute('aria-hidden', 'true');
          button.appendChild(badge);
        }
        badge.textContent = String(rangeCount(button.dataset.rangeFilter || 'all'));
      });
    };
    updateRangeCounts();

    // Mobile Echo cover-flow carousel: neighbouring brands remain visible so
    // the control reads immediately as swipeable, while preserving auto brands/counts.
    const brandWrap = root.querySelector('[data-brand-buttons]') || root.querySelector('.echo-brand-search__buttons');
    const brandOnlyChips = chips.filter((chip) => normalize(chip.dataset.brandFilter));

    // On mobile, keep RESET outside the horizontally scrolling cover-flow track.
    // Absolute positioning inside an overflow scroller made RESET travel with/overlap
    // the first brand (for example Armaf). Moving the existing button preserves its
    // filter handler while giving it a stable position above the carousel.
    const resetChip = chips.find((chip) => !normalize(chip.dataset.brandFilter));
    if (brandWrap && resetChip && window.matchMedia('(max-width: 749px)').matches) {
      let resetRow = root.querySelector('.echo-brand-reset-mobile-row');
      if (!resetRow) {
        resetRow = document.createElement('div');
        resetRow.className = 'echo-brand-reset-mobile-row';
        brandWrap.parentNode.insertBefore(resetRow, brandWrap);
      }
      resetRow.appendChild(resetChip);
    }

    // Mobile draggable progress slider for the cover-flow carousel.
    // It stays in sync with swipe scrolling and lets customers scrub quickly
    // through the full brand list without replacing normal touch gestures.
    let brandSlider = null;
    let sliderDragging = false;
    if (brandWrap) {
      let sliderRow = root.querySelector('.echo-brand-slider-row');
      if (!sliderRow) {
        sliderRow = document.createElement('div');
        sliderRow.className = 'echo-brand-slider-row';
        sliderRow.innerHTML = '<input class=\"echo-brand-slider\" type=\"range\" min=\"0\" max=\"1000\" value=\"0\" step=\"1\" aria-label=\"Browse brands\">';
        brandWrap.insertAdjacentElement('afterend', sliderRow);
      }
      brandSlider = sliderRow.querySelector('.echo-brand-slider');
      if (brandSlider) {
        const setFromSlider = () => {
          const maxScroll = Math.max(0, brandWrap.scrollWidth - brandWrap.clientWidth);
          brandWrap.scrollLeft = maxScroll * (Number(brandSlider.value) / 1000);
        };
        brandSlider.addEventListener('input', setFromSlider);
        brandSlider.addEventListener('pointerdown', () => { sliderDragging = true; });
        brandSlider.addEventListener('touchstart', () => { sliderDragging = true; }, { passive: true });
        const finishSlider = () => {
          sliderDragging = false;
          updateCarouselCenter();
          if (lastCarouselCenter) brandWrap.scrollTo({ left: centreTargetFor(lastCarouselCenter), behavior: 'smooth' });
        };
        brandSlider.addEventListener('change', finishSlider);
        brandSlider.addEventListener('pointerup', finishSlider);
        brandSlider.addEventListener('touchend', finishSlider, { passive: true });
      }
    }

    // Compact reset control beneath the brand slider. Kept outside the carousel
    // so it never overlaps or scrolls with a brand card.
    let compactReset = root.querySelector('.echo-brand-reset-compact');
    if (!compactReset && brandWrap) {
      compactReset = document.createElement('button');
      compactReset.type = 'button';
      compactReset.className = 'echo-brand-reset-compact';
      compactReset.innerHTML = '<span aria-hidden="true">↻</span> RESET';
      const sliderRow = root.querySelector('.echo-brand-slider-row');
      (sliderRow || brandWrap).insertAdjacentElement('afterend', compactReset);
    }

    let lastCarouselCenter = null;

    const centreTargetFor = (chip) => {
      if (!brandWrap || !chip) return 0;
      const maxScroll = Math.max(0, brandWrap.scrollWidth - brandWrap.clientWidth);
      return Math.max(0, Math.min(maxScroll, chip.offsetLeft + chip.offsetWidth / 2 - brandWrap.clientWidth / 2));
    };

    const updateCarouselCenter = () => {
      if (!brandWrap || !brandOnlyChips.length) return;
      const wr = brandWrap.getBoundingClientRect();
      const centre = wr.left + wr.width / 2;
      let nearest = null;
      let distance = Infinity;
      brandOnlyChips.forEach((chip) => {
        const r = chip.getBoundingClientRect();
        const d = Math.abs((r.left + r.width / 2) - centre);
        if (d < distance) { distance = d; nearest = chip; }
      });
      brandOnlyChips.forEach((chip) => chip.classList.toggle('is-wheel-center', chip === nearest));
      lastCarouselCenter = nearest;
      if (brandSlider && !sliderDragging) {
        const maxScroll = Math.max(0, brandWrap.scrollWidth - brandWrap.clientWidth);
        const ratio = maxScroll ? (brandWrap.scrollLeft / maxScroll) : 0;
        brandSlider.value = String(Math.round(ratio * 1000));
        const row = brandSlider.closest('.echo-brand-slider-row');
        if (row) row.hidden = maxScroll < 2;
      }
    };

    if (brandWrap) {
      let raf = 0;
      let settleTimer = 0;
      brandWrap.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(updateCarouselCenter);
        clearTimeout(settleTimer);
        if (!sliderDragging) {
          settleTimer = setTimeout(() => {
            updateCarouselCenter();
            if (lastCarouselCenter) brandWrap.scrollTo({ left: centreTargetFor(lastCarouselCenter), behavior: 'smooth' });
          }, 120);
        }
      }, { passive: true });
      requestAnimationFrame(() => {
        // Start the carousel around the dynamic middle of the current brand list.
        // This is visual positioning only: selectedBrand remains empty, so all
        // products continue to show until the customer explicitly selects a brand.
        const middleChip = brandOnlyChips[Math.floor((brandOnlyChips.length - 1) / 2)] || brandOnlyChips[0];
        if (middleChip) {
          brandWrap.scrollLeft = centreTargetFor(middleChip);
        }
        updateCarouselCenter();
      });
    }

    const apply = () => {
      let visible = 0;
      cards.forEach((card) => {
        const brandMatch = !selectedBrand || (card.dataset.brandSearchBrand || '') === selectedBrand;
        const isNew = isCardNew(card);
        const isPopular = card.dataset.productPopular === 'true';
        const isBundle = card.dataset.productBundle === 'true';
        const isSamples = card.dataset.productSamples === 'true';
        const isBundle15 = card.dataset.productBundle15 === 'true';
        const isBundle30 = card.dataset.productBundle30 === 'true';
        const isHybrid = card.dataset.productHybrid === 'true';
        const isMale = card.dataset.productMale === 'true';
        const isFemale = card.dataset.productFemale === 'true';
        const isUnisex = card.dataset.productUnisex === 'true';
        const rangeMatch = rangeFilter === 'all'
          || (rangeFilter === 'new' && isNew)
          || (rangeFilter === 'popular' && isPopular)
          || (rangeFilter === 'samples' && isSamples)
          || (rangeFilter === 'bundle15' && isBundle15)
          || (rangeFilter === 'bundle30' && isBundle30)
          || (rangeFilter === 'hybrid' && isHybrid)
          || (rangeFilter === 'male' && isMale)
          || (rangeFilter === 'female' && isFemale)
          || (rangeFilter === 'unisex' && isUnisex);
        const match = brandMatch && rangeMatch;
        card.hidden = !match;
        if (match) visible += 1;
      });

      rangeButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.rangeFilter === rangeFilter);
      });

      chips.forEach((chip) => {
        chip.classList.toggle('is-active', normalize(chip.dataset.brandFilter) === selectedBrand);
      });

      if (empty) empty.hidden = visible !== 0;
      grid.classList.toggle('is-brand-filtered', Boolean(selectedBrand));
    };

    rangeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        rangeFilter = button.dataset.rangeFilter || 'all';
        apply();
      });
    });

    if (compactReset) {
      compactReset.addEventListener('click', () => {
        selectedBrand = '';
        apply();
        if (brandWrap && brandOnlyChips[0]) {
          brandWrap.scrollTo({ left: centreTargetFor(brandOnlyChips[0]), behavior: 'smooth' });
        }
      });
    }

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        selectedBrand = normalize(chip.dataset.brandFilter);
        apply();
        if (window.matchMedia('(max-width: 749px)').matches && selectedBrand && brandWrap) {
          const target = centreTargetFor(chip);
          brandWrap.scrollTo({ left: target, behavior: 'smooth' });
        } else if (selectedBrand && grid.scrollIntoView) {
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    apply();
  };

  const initAll = () => document.querySelectorAll('[data-brand-search]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
